import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { monitoring } from '@/lib/monitoring';
import { useStableAuth } from '@/hooks/useStableAuth';
import type { 
  BankIntegration, 
  BankProvider, 
  BankEnvironment,
  BankIntegrationCredentials 
} from '@/types/bankIntegration';

const INTEGRATIONS_ERROR_TOAST = {
  title: 'Não foi possível concluir a operação',
  description: 'Tente novamente. Se o problema persistir, entre em contato com o suporte.',
  variant: 'destructive' as const,
};

export const useBankIntegrations = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { companyId, userId } = useStableAuth();

  // Buscar integrações da empresa
  const {
    data: integrations,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['bank-integrations', companyId],
    queryFn: async () => {
      if (!companyId) return [];
      
      const { data, error } = await supabase
        .from('bank_integrations')
        .select('*')
        .eq('company_id', companyId);

      if (error) throw error;
      
      return data as BankIntegration[];
    },
    enabled: !!companyId,
  });

  // Buscar integração específica por provider
  const getIntegrationByProvider = (provider: BankProvider): BankIntegration | undefined => {
    return integrations?.find(i => i.provider === provider);
  };

  // Upload de certificado para o bucket
  const uploadCertificate = async (
    file: File, 
    fileType: 'cert' | 'key'
  ): Promise<string> => {
    if (!companyId) throw new Error('Company ID não encontrado');

    const extension = fileType === 'cert' ? 'crt' : 'key';
    const fileName = `inter_${fileType}.${extension}`;
    const filePath = `${companyId}/inter/${fileName}`;

    // Remove arquivo existente se houver
    await supabase.storage.from('bank-certs').remove([filePath]);

    const { error } = await supabase.storage
      .from('bank-certs')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: true,
      });

    if (error) throw error;

    return filePath;
  };

  // Criar ou atualizar integração
  const upsertIntegrationMutation = useMutation({
    mutationFn: async ({
      provider,
      credentials,
      environment = 'sandbox',
      pixKey,
      defaultFinePercent,
      defaultInterestPercent,
      validityAfterDue,
      bankCode,
      agencyNumber,
      agencyDigit,
      accountNumber,
      accountDigit,
      walletCode,
      agreementNumber,
      cnabLayout,
      label,
      status,
      suppressSuccessToast,
    }: {
      provider: BankProvider;
      credentials?: BankIntegrationCredentials;
      environment?: BankEnvironment;
      pixKey?: string;
      defaultFinePercent?: number;
      defaultInterestPercent?: number;
      validityAfterDue?: number;
      bankCode?: string | null;
      agencyNumber?: string | null;
      agencyDigit?: string | null;
      accountNumber?: string | null;
      accountDigit?: string | null;
      walletCode?: string | null;
      agreementNumber?: string | null;
      cnabLayout?: string | null;
      label?: string | null;
      status?: 'active' | 'inactive' | 'pending' | 'error';
      /** Omitir toast padrão "Integração salva" — para fluxos que mostram mensagem própria (ex.: Inter + webhook) */
      suppressSuccessToast?: boolean;
    }) => {
      if (!companyId || !userId) throw new Error('Usuário não autenticado');

      const existingIntegration = getIntegrationByProvider(provider);

      const integrationData = {
        credentials: (credentials || {}) as any,
        environment,
        pix_key: pixKey || null,
        status: status || 'active',
        updated_at: new Date().toISOString(),
        default_fine_percent: defaultFinePercent ?? null,
        default_interest_percent: defaultInterestPercent ?? null,
        validity_after_due: validityAfterDue ?? null,
        bank_code: bankCode ?? null,
        agency_number: agencyNumber ?? null,
        agency_digit: agencyDigit ?? null,
        account_number: accountNumber ?? null,
        account_digit: accountDigit ?? null,
        wallet_code: walletCode ?? null,
        agreement_number: agreementNumber ?? null,
        cnab_layout: cnabLayout ?? null,
        label: label ?? null,
      };

      if (existingIntegration) {
        // Atualizar
        const { data, error } = await supabase
          .from('bank_integrations')
          .update(integrationData)
          .eq('id', existingIntegration.id)
          .select()
          .single();

        if (error) throw error;
        return data;
      } else {
        // Criar - construir objeto tipado para inserção
        const insertData: any = {
          company_id: companyId,
          provider,
          created_by: userId,
          credentials: credentials || {},
          environment,
          pix_key: pixKey || null,
          status: status || 'active',
          default_fine_percent: defaultFinePercent ?? null,
          default_interest_percent: defaultInterestPercent ?? null,
          validity_after_due: validityAfterDue ?? null,
          bank_code: bankCode ?? null,
          agency_number: agencyNumber ?? null,
          agency_digit: agencyDigit ?? null,
          account_number: accountNumber ?? null,
          account_digit: accountDigit ?? null,
          wallet_code: walletCode ?? null,
          agreement_number: agreementNumber ?? null,
          cnab_layout: cnabLayout ?? null,
          label: label ?? null,
        };

        const { data, error } = await supabase
          .from('bank_integrations')
          .insert(insertData)
          .select()
          .single();

        if (error) throw error;
        return data;
      }
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['bank-integrations', companyId] });
      if (!variables.suppressSuccessToast) {
        toast({
          title: 'Integração salva',
          description: 'As credenciais foram salvas com sucesso.',
        });
      }
    },
    onError: (error: Error) => {
      console.error('[useBankIntegrations] upsertIntegration:', error);
      monitoring.captureException(error, { context: 'upsertIntegration' });
      toast(INTEGRATIONS_ERROR_TOAST);
    },
  });

  // Desativar integração
  const deactivateIntegrationMutation = useMutation({
    mutationFn: async (integrationId: string) => {
      const { error } = await supabase
        .from('bank_integrations')
        .update({ status: 'inactive', updated_at: new Date().toISOString() })
        .eq('id', integrationId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bank-integrations', companyId] });
      toast({
        title: 'Integração desativada',
        description: 'A integração foi desativada com sucesso.',
      });
    },
    onError: (error: Error) => {
      console.error('[useBankIntegrations] deactivateIntegration:', error);
      monitoring.captureException(error, { context: 'deactivateIntegration' });
      toast(INTEGRATIONS_ERROR_TOAST);
    },
  });

  // Trocar code OAuth por tokens (Cora)
  const exchangeCoraTokenMutation = useMutation({
    mutationFn: async ({ code, redirectUri }: { code: string; redirectUri: string }) => {
      if (!companyId) throw new Error('Company ID não encontrado');

      const { data, error } = await supabase.functions.invoke('exchange-cora-token', {
        body: { code, redirect_uri: redirectUri, company_id: companyId }
      });

      if (error) throw error;
      if (!data.success) throw new Error(data.error);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bank-integrations', companyId] });
    },
    onError: (error: Error) => {
      console.error('[useBankIntegrations] exchange-cora-token:', error);
      monitoring.captureException(error, { context: 'exchange-cora-token' });
      toast(INTEGRATIONS_ERROR_TOAST);
    },
  });

  return {
    integrations,
    isLoading,
    error,
    getIntegrationByProvider,
    uploadCertificate,
    upsertIntegration: upsertIntegrationMutation.mutate,
    upsertIntegrationAsync: upsertIntegrationMutation.mutateAsync,
    isUpserting: upsertIntegrationMutation.isPending,
    deactivateIntegration: deactivateIntegrationMutation.mutate,
    isDeactivating: deactivateIntegrationMutation.isPending,
    exchangeCoraToken: exchangeCoraTokenMutation.mutateAsync,
    isExchangingCoraToken: exchangeCoraTokenMutation.isPending,
  };
};
